import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { PortfolioKpiResponse, Study, StudyMilestones } from '@models';
import { UserDataService } from '@services';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class StudyService {
  private http: HttpClient = inject(HttpClient);
  private userDataService: UserDataService = inject(UserDataService);
  private params: HttpParams = new HttpParams();

  private clientNamesList: string[] = [];

  // Caching mechanism for studies portfolio
  private cachedStudiesPortfolio$ = new Map<string, Observable<Study[]>>();
  private studiesSubject = new BehaviorSubject<Study[] | null>(null);
  private currentCacheKey: string | null = null;

  constructor() {
    const clientNames = this.userDataService.getStandardClientNames();
    const orgIds = this.userDataService.getUserOrgIds();
    const username = this.userDataService.getUsername();

    this.clientNamesList = clientNames || [];

    this.params = this.params.set('org_ids', orgIds?.join(',') ?? '');
    this.params = this.params.set('user_id', username ?? '');
  }

  getPortfolioKpi(
    studyList: string[],
    standardClientNames: string[]
  ): Observable<PortfolioKpiResponse> {
    const BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
    const GET_PORTFOLIO_KPI_URL = `${BASE_URL}/crg-sponsor-portal-service/api/v1/studies/portfolio/kpis`;

    const params = this.params
      .set('study_sk_list', studyList.join(','))
      .set('standard_client_names', encodeURIComponent(JSON.stringify(standardClientNames)));

    return this.http.get<PortfolioKpiResponse>(GET_PORTFOLIO_KPI_URL, {
      withCredentials: true,
      params: params
    });
  }

  getMilestones(studySk: string): Observable<StudyMilestones> {
    const BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
    const GET_STUDY_MILESTONES_URL = `${BASE_URL}/crg-sponsor-portal-service/api/v1/studies/${studySk}/milestones`;
    const standardClientNamesJson = encodeURIComponent(
      JSON.stringify(this.getClientNamesList() ?? [])
    );
    const params = this.params.set('standard_client_names', standardClientNamesJson);
    return this.http.get<StudyMilestones>(GET_STUDY_MILESTONES_URL, {
      withCredentials: true,
      params: params
    });
  }

  getClientNamesList(): string[] {
    return this.clientNamesList;
  }

  getStandardClientNames(): string[] {
    return this.userDataService.getStandardClientNames() || [];
  }

  getStudiesPortfolio(standardClientNames: string[]): Observable<Study[]> {
    const cacheKey = JSON.stringify(standardClientNames);

    if (!this.cachedStudiesPortfolio$.has(cacheKey)) {
      const BASE_URL = environment.CRG_DIGITALIZATION_SERVICE_URL;
      const GET_STUDIES_PORTFOLIO_URL = `${BASE_URL}/crg-sponsor-portal-service/api/v1/studies/portfolio`;

      const params = this.params.set(
        'standard_client_names',
        encodeURIComponent(JSON.stringify(standardClientNames))
      );

      const studiesObservable$ = this.http
        .get<any[]>(GET_STUDIES_PORTFOLIO_URL, {
          withCredentials: true,
          params: params
        })
        .pipe(
          map((studies: any[]) =>
            studies.map((study) => ({
              ...study,
              studySk: study.studySk?.toString()
            }))
          ),
          tap((studies: Study[]) => {
            if (this.currentCacheKey === cacheKey) {
              this.studiesSubject.next(studies);
            }
          }),
          shareReplay(1)
        );

      this.cachedStudiesPortfolio$.set(cacheKey, studiesObservable$);
    }

    if (this.currentCacheKey !== cacheKey) {
      this.currentCacheKey = cacheKey;
      const cachedObservable = this.cachedStudiesPortfolio$.get(cacheKey);
      if (cachedObservable) {
        cachedObservable.pipe(tap((studies) => this.studiesSubject.next(studies))).subscribe();
      }
    }

    return this.cachedStudiesPortfolio$.get(cacheKey)!;
  }

  getCachedStudiesPortfolio(): BehaviorSubject<Study[] | null> {
    return this.studiesSubject;
  }

  clearStudiesPortfolioCache(standardClientNames?: string[]): void {
    if (standardClientNames) {
      const cacheKey = standardClientNames.join(',');
      this.cachedStudiesPortfolio$.delete(cacheKey);

      if (this.currentCacheKey === cacheKey) {
        this.currentCacheKey = null;
        this.studiesSubject.next(null);
      }
    } else {
      this.cachedStudiesPortfolio$.clear();
      this.currentCacheKey = null;
      this.studiesSubject.next(null);
    }
  }
}
